import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, money0, shortDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill, useBackdropDismiss } from "../components/UI";
import { useAuth } from "../lib/auth";
import { can } from "../lib/roles";

function MonthEndXeroRetry({ year, month, lines, onDone }) {
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null); // {tone, text}
  const [result, setResult] = useState(null);
  const posted = lines.filter(l => l.xero_invoice_id).length;
  const failed = lines.filter(l => l.xero_post_error && !l.xero_invoice_id);
  const unposted = lines.filter(l => !l.xero_invoice_id && !l.xero_post_error);

  const retry = async () => {
    setBusy(true); setFlash(null);
    try {
      const r = await api.post(`/month-end/${year}/${month}/post-to-xero`);
      setResult(r.data);
      setFlash({tone:"green", text: r.data.message});
      onDone && onDone();
    } catch (e) {
      setFlash({tone:"red", text: formatApiErrorDetail(e.response?.data?.detail) || e.message});
    } finally { setBusy(false); }
  };

  const hasWork = failed.length > 0 || unposted.length > 0;

  return (
    <div className="ach-card" style={{marginBottom: 12}} data-testid="me-xero-panel">
      <h2 className="ach-card__title" style={{margin:0}}>Xero posting</h2>
      <div style={{fontSize:13, color:"var(--ach-text-1)", marginTop:6}}>
        <span style={{marginRight:14}}>Posted to Xero: <strong data-testid="me-xero-posted-count">{posted}</strong> of {lines.length}</span>
        {failed.length > 0 && <span style={{marginRight:14, color:"var(--ach-red)"}}>Failed: <strong data-testid="me-xero-failed-count">{failed.length}</strong></span>}
        {unposted.length > 0 && <span style={{color:"var(--ach-text-2)"}}>Not yet posted: <strong data-testid="me-xero-unposted-count">{unposted.length}</strong></span>}
      </div>
      {hasWork && (
        <div style={{marginTop:10}}>
          <button className="ach-btn ach-btn--primary" onClick={retry} disabled={busy} data-testid="btn-retry-xero-post">
            {busy ? "Posting…" : `Post the ${failed.length + unposted.length} remaining line${failed.length + unposted.length === 1 ? "" : "s"} to Xero`}
          </button>
        </div>
      )}
      {failed.length > 0 && (
        <div style={{marginTop:12, padding:10, background:"var(--ach-red-tint)", borderLeft:"3px solid var(--ach-red)", fontSize:12}} data-testid="me-xero-failed-list">
          <div style={{fontWeight:600, color:"var(--ach-red)", marginBottom:6}}>Lines that failed to post:</div>
          <ul style={{margin:0, paddingLeft:18}}>
            {failed.slice(0, 8).map(l => (
              <li key={l.id}>{l.calculation}: <em>{l.xero_post_error}</em></li>
            ))}
            {failed.length > 8 && <li>…and {failed.length - 8} more</li>}
          </ul>
        </div>
      )}
      {result && (
        <div style={{marginTop:10, fontSize:12, color:"var(--ach-text-2)"}} data-testid="me-xero-retry-result">
          Last run: {result.posted} lines posted across {result.customers_billed} customer invoices
          {result.customer_errors ? `, ${result.customer_errors} failed` : ""}.
        </div>
      )}
      {flash && (
        <div style={{marginTop:8, padding:8, background: flash.tone === "green" ? "var(--ach-green-tint)" : "var(--ach-red-tint)", color: flash.tone === "green" ? "var(--ach-green)" : "var(--ach-red)", borderRadius:6, fontSize:12}}>
          {flash.text}
        </div>
      )}
    </div>
  );
}


function AdjustmentDialog({ monthLabel, adjAmount, setAdjAmount, adjReason, setAdjReason, adjErr, adjBusy, onClose, onSave }) {
  const isDirty = adjAmount !== "" || adjReason.trim() !== "";
  const dismiss = useBackdropDismiss(isDirty, onClose);
  const requestClose = () => {
    if (isDirty && !window.confirm("Discard the changes you just typed?")) return;
    onClose();
  };
  return (
    <div className="ach-modal-backdrop" data-testid="adjustment-dialog" {...dismiss}>
      <div className="ach-modal" style={{maxWidth: 520}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Add an adjustment to {monthLabel}</h2></div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Use a positive amount to add a charge; a negative amount to credit money back. The locked charges above are untouched.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>Amount ($) <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input type="number" step="0.01" value={adjAmount} onChange={(e)=>setAdjAmount(e.target.value)}
                placeholder="e.g. 250 or -150" data-testid="adjustment-amount" />
              <div className="helper">Positive adds, negative credits.</div>
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Reason <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input value={adjReason} onChange={(e)=>setAdjReason(e.target.value)}
                placeholder="e.g. Missed transport charge on ACH302 — customer agreed" data-testid="adjustment-reason" />
            </div>
            {adjErr && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{adjErr}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={requestClose} data-testid="adjustment-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={onSave}
            disabled={adjBusy || !adjAmount || !adjReason.trim()} data-testid="adjustment-go">
            {adjBusy ? "Saving…" : "Save adjustment →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function pillTone(pill) {
  if (pill.includes("part-month")) return "amber";
  if (pill.includes("tier-one")) return "blue";
  if (pill.includes("weekly")) return "neutral";
  if (pill.includes("custom")) return "neutral";
  return "green";
}

// Send email summary — was previously inlined into the MonthEnd page. Extracted
// so the "which lines to include" checkbox list can own its own local state,
// and to fix a nested-<label> HTML bug that was rendering rows as oversized
// empty boxes with missing text in some browsers.
function SendSummaryDialog({ data, year, month, onClose }) {
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [includeAll, setIncludeAll] = useState(true);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isDirty = comment.trim() !== "" || email.trim() !== "" || !includeAll || Object.values(selected).some(Boolean);
  const dismiss = useBackdropDismiss(isDirty, onClose);

  const lines = data.lines || [];
  const tickedCount = Object.values(selected).filter(Boolean).length;
  const selectAll = () => {
    const next = {}; lines.forEach(l => { next[l.hire_id] = true; }); setSelected(next);
  };
  const selectNone = () => setSelected({});
  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const body = { comment: comment.trim(), to_email: email.trim() };
      if (!includeAll) {
        body.include_hire_ids = Object.keys(selected).filter(k => selected[k]);
        if (body.include_hire_ids.length === 0) {
          setErr("Tick at least one machine to include."); setBusy(false); return;
        }
      }
      await api.post(`/month-end/${year}/${month}/send-summary`, body);
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="send-summary-dialog" {...dismiss}>
      <div className="ach-modal" style={{maxWidth:560}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Send email summary for {data.month_label}</h2>
        </div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Adds an optional note above the summary table, then sends via Resend (if switched on) or logs a would-send if it&apos;s not.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label htmlFor="ms-email-input">Send to (email address)</label>
              <input id="ms-email-input" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="ms-email" placeholder="accounts@hyjac.com.au" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label htmlFor="ms-comment-input">Comment (optional — appears above the table)</label>
              <textarea id="ms-comment-input" rows={4} value={comment} onChange={(e)=>setComment(e.target.value)} data-testid="ms-comment" placeholder="e.g. Hi Andy — please note ACH421 came off Sept 3, adjusted total below." />
            </div>

            {/* Which lines to include. Deliberately a <div> not a <label> — nested
                labels break input↔label association and were the cause of the
                oversized-empty-checkbox rendering bug in earlier builds. */}
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <div style={{fontSize:12, fontWeight:600, color:"var(--ach-text-1)", marginBottom:6}}>Which lines to include?</div>

              <label style={{display:"flex", gap:8, alignItems:"center", fontSize:12, marginBottom:4, cursor:"pointer"}}>
                <input type="radio" name="ms-include-mode" checked={includeAll} onChange={()=>setIncludeAll(true)}
                       data-testid="ms-include-all" style={{width:14, height:14, flexShrink:0, margin:0}} />
                <span>All {lines.length} lines in this month-end</span>
              </label>
              <label style={{display:"flex", gap:8, alignItems:"center", fontSize:12, cursor:"pointer"}}>
                <input type="radio" name="ms-include-mode" checked={!includeAll} onChange={()=>setIncludeAll(false)}
                       data-testid="ms-include-selected" style={{width:14, height:14, flexShrink:0, margin:0}} />
                <span>Only the machines I tick below</span>
              </label>

              {!includeAll && (
                <div style={{marginTop:10}}>
                  {/* Select-all / select-none convenience — because ticking 20+ lines by hand is tedious. */}
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                               padding:"6px 8px", background:"var(--ach-page-bg)", borderRadius:6, marginBottom:4, fontSize:12}}>
                    <span style={{color:"var(--ach-text-2)"}} data-testid="ms-picker-count">
                      {tickedCount} of {lines.length} ticked
                    </span>
                    <div style={{display:"flex", gap:6}}>
                      <button type="button" className="ach-btn ach-btn--grey" style={{padding:"3px 10px", fontSize:11}}
                              onClick={selectAll} disabled={tickedCount === lines.length}
                              data-testid="ms-select-all">Select all</button>
                      <button type="button" className="ach-btn ach-btn--grey" style={{padding:"3px 10px", fontSize:11}}
                              onClick={selectNone} disabled={tickedCount === 0}
                              data-testid="ms-select-none">Select none</button>
                    </div>
                  </div>
                  <div style={{maxHeight:220, overflowY:"auto", border:"1px solid var(--ach-row-divider)",
                               borderRadius:6, padding:"4px 0", background:"#fff"}} data-testid="ms-line-picker">
                    {lines.map(l => (
                      <label key={l.hire_id} data-testid={`ms-pick-${l.unit_number}`}
                             style={{display:"flex", gap:10, alignItems:"center", padding:"6px 10px",
                                     fontSize:12, cursor:"pointer", borderBottom:"1px solid var(--ach-row-divider)"}}>
                        <input type="checkbox" checked={!!selected[l.hire_id]} onChange={()=>toggle(l.hire_id)}
                               style={{width:14, height:14, flexShrink:0, margin:0}} />
                        <span style={{flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                          <strong>{l.unit_number}</strong>
                          {" — "}
                          {l.customer_name}
                          <span style={{color:"var(--ach-text-2)"}}> ({l.owning_company})</span>
                        </span>
                        <span style={{color:"var(--ach-text-2)", flexShrink:0}}>{money(l.amount)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="ms-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" disabled={busy} onClick={submit} data-testid="ms-send">
            {busy ? "Sending…" : "Send / log →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LineOverrideDialog({ line, monthStr, onClose, onSaved }) {
  const [hire, setHire] = useState(null);
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState("");
  const [initial, setInitial] = useState({ selected: {}, reason: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/hires/${line.hire_id}`).then(r => {
      setHire(r.data);
      // Pre-populate selected map from any existing override for this month
      const existing = (r.data.month_overrides || []).find(o => o.month === monthStr);
      if (existing) {
        const m = {};
        (existing.zeroed_charge_ids || []).forEach(id => { m[id] = true; });
        setSelected(m);
        const reasonVal = existing.reason || "";
        if (reasonVal) setReason(reasonVal);
        // Snapshot what we opened with so we can detect real user changes below.
        setInitial({ selected: m, reason: reasonVal });
      }
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.hire_id]);

  const isDirty = reason !== initial.reason
    || JSON.stringify(Object.keys(selected).filter(k=>selected[k]).sort())
       !== JSON.stringify(Object.keys(initial.selected).filter(k=>initial.selected[k]).sort());
  const dismiss = useBackdropDismiss(isDirty, onClose);

  const zeroable = hire ? [
    ...(hire.one_off_charges || []).filter(o => (o.charge_month || "") === monthStr).map(o => ({
      id: o.id, label: `${o.description} (one-off)`, amount: o.amount,
    })),
    ...(hire.custom_charges || []).map(c => ({
      id: c.id,
      label: `${c.description} @ $${Number(c.rate).toLocaleString()}${c.kind === "one-off" ? " (one-off)" : "/wk"} × ${c.qty || 1}`,
      amount: (c.kind === "one-off" ? Number(c.rate) * Number(c.qty || 1) : null),
    })),
  ] : [];

  const save = async () => {
    if (!reason.trim()) { setErr("Give a reason so the override is auditable."); return; }
    setBusy(true); setErr("");
    try {
      const zeroed = Object.keys(selected).filter(k => selected[k]);
      await api.post(`/hires/${line.hire_id}/month-overrides`, {
        month: monthStr,
        zeroed_charge_ids: zeroed,
        reason: reason.trim(),
      });
      onSaved();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="line-override-dialog" {...dismiss}>
      <div className="ach-modal" style={{maxWidth:560}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <div>
            <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Adjust charge lines — {line.unit_number}</h2>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:2}}>Month {monthStr} — {line.customer_name}</div>
          </div>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}} data-testid="ov-close">Close</button>
        </div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Tick anything the customer&apos;s handling directly or the owning entity is absorbing.
            <br /><strong>This is a one-off override for {monthStr} only</strong> — the base weekly hire rate stays as-is; only the extra lines below can be zeroed.
          </p>
          {!hire ? (
            <div style={{padding:12, fontSize:12, color:"var(--ach-text-2)"}}>Loading charge lines…</div>
          ) : zeroable.length === 0 ? (
            <div style={{padding:12, background:"var(--ach-page-bg)", borderRadius:6, fontSize:12, color:"var(--ach-text-2)"}}>
              No adjustable charge lines on this hire — only the base hire rate is billed this month, and that can&apos;t be zeroed here (use a rate change or a month adjustment).
            </div>
          ) : (
            <div data-testid="ov-lines">
              {zeroable.map(z => (
                <label key={z.id} style={{display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderBottom:"1px solid var(--ach-row-divider)", fontSize:13, cursor:"pointer"}} data-testid={`ov-line-${z.id}`}>
                  <input type="checkbox" checked={!!selected[z.id]} onChange={(e)=>setSelected({...selected, [z.id]: e.target.checked})} data-testid={`ov-check-${z.id}`} />
                  <span style={{flex:1}}>{z.label}</span>
                  {z.amount != null && <span style={{color:"var(--ach-text-2)"}}>${Number(z.amount).toLocaleString()}</span>}
                </label>
              ))}
            </div>
          )}
          <div className="ach-field" style={{marginTop:12}}>
            <label>Reason (audit trail)</label>
            <input value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="e.g. Customer arranging own transport this month" data-testid="ov-reason" />
          </div>
          {err && <div style={{color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12, marginTop:10}}>{err}</div>}
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="ov-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy || !hire || !reason.trim()} data-testid="ov-save">
            {busy ? "Saving…" : "Apply override →"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function MonthEnd() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // 7 Sep 2026 send/close split — two independent actions:
  //   Send: batch-post reviewed unsent lines to Xero (Director/Office/Accounts, any day)
  //   Close: lock the run + build entity totals (Director only, once)
  const [sendResult, setSendResult] = useState(null);
  const [closeResult, setCloseResult] = useState(null);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeAckUnsent, setCloseAckUnsent] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjErr, setAdjErr] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [overrideFor, setOverrideFor] = useState(null); // line being adjusted
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    // Do NOT setData(null) — that unmounts the entire month-end table and
    // forces the browser to scroll back to the top of the page every time
    // a review checkbox is ticked or a save completes. The previous copy
    // of load() cleared data first; that "fix" (probably chasing a
    // stale-render mirage) is the regression Todd flagged 9 Sep 2026.
    // React reconciles the updated array in place, preserving scroll.
    api.get(`/month-end/${year}/${month}`).then(r => setData(r.data));
  };

  useEffect(() => { load(); }, [year, month]);

  const openSendConfirm  = () => setSendConfirmOpen(true);
  const openCloseConfirm = () => { setCloseAckUnsent(false); setCloseConfirmOpen(true); };

  const doSend = async () => {
    setSendConfirmOpen(false);
    setBusy(true); setErr("");
    try {
      const hireIds = (data.lines || [])
        .filter(l => l.reviewed === true && !l.xero_invoice_id)
        .map(l => l.hire_id);
      const r = await api.post(`/month-end/${year}/${month}/send`, { hire_ids: hireIds });
      setSendResult(r.data);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const doClose = async () => {
    setCloseConfirmOpen(false);
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/month-end/${year}/${month}/close`, { acknowledge_unsent: closeAckUnsent });
      setCloseResult(r.data);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const shiftMonth = (delta) => {
    let y = year, m = month + delta;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setYear(y); setMonth(m);
  };

  if (!data) return <div style={{padding: 40, color:"var(--ach-text-2)"}}>Loading…</div>;

  const t = data.totals;
  const locked = data.status === "locked" || data.status === "approved";

  const formatApprovedAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const day = d.getDate();
    const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, "0");
    const suffix = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    return `${day} ${monthShort} ${hours}:${mins}${suffix}`;
  };

  const canSend  = can(user, "month-end.send");
  const canClose = can(user, "month-end.close");

  // Sendable lines = ticked AND not yet sent. Server stamps each with a
  // batch_key computed by _group_lines_into_batches — the SAME helper the
  // send path uses. We group on the server's key rather than deriving our
  // own from customer_id, so the count and totals in the confirm dialog
  // cannot disagree with what the send will actually create. Historical
  // bug (8 Sep 2026): grouping-by-customer here under-reported by three
  // invoices for Sep 2026, because General Crane Services WA's
  // consolidate_invoices=false override splits four hires into four
  // invoices but this UI treated them as one.
  const sendableLines = (data.lines || []).filter(l => l.reviewed === true && !l.xero_invoice_id);
  const sendableByBatch = {};
  for (const l of sendableLines) {
    // batch_key falls back to customer_id for older servers that haven't
    // shipped the enrichment yet — same behaviour as before this patch,
    // so a version mismatch degrades to the old (wrong) count rather than
    // a blank screen. Once the server has been upgraded this branch is
    // never taken.
    const key = l.batch_key || l.customer_id;
    if (!sendableByBatch[key]) {
      sendableByBatch[key] = { name: l.customer_name || "—", total: 0, count: 0 };
    }
    sendableByBatch[key].total += l.amount || 0;
    sendableByBatch[key].count += 1;
  }
  const sendableCustomers = Object.values(sendableByBatch)
    .map(v => ({ ...v, total: Math.round(v.total * 100) / 100 }))
    .sort((a, b) => b.total - a.total);
  const sendableTotal = Math.round(sendableCustomers.reduce((a, c) => a + c.total, 0) * 100) / 100;
  const sendableInvoiceCount = sendableCustomers.length;

  // At-close set — everything not yet in Xero. Drives the close warning.
  const unsentAtClose = (data.lines || []).filter(l => !l.xero_invoice_id);
  const unsentUnits = [...new Set(unsentAtClose.map(l => l.unit_number))].sort();
  const canAdjust = can(user, "month-end.adjustments") && locked;

  // Split the run into what's already been billed vs what's still to go
  // out. The month-end page has to make what's ALREADY sent visible rather
  // than hide it — a screen that only shows the remainder makes a real
  // Xero post look like it never happened, which is exactly the shape of
  // the bug that took ACH302 to the edge of double-billing.
  const sentLines = (data.lines || []).filter(l => !!l.xero_invoice_id);
  const sentTotal = Math.round(sentLines.reduce((a, l) => a + (l.amount || 0), 0) * 100) / 100;
  const remainingLines = (data.lines || []).filter(l => !l.xero_invoice_id);
  const remainingTotal = Math.round(remainingLines.reduce((a, l) => a + (l.amount || 0), 0) * 100) / 100;
  // Distinct customer invoices already sent — used for a compact restated
  // header line ('sent to N customer invoices').
  const sentInvoiceNumbers = [...new Set(sentLines.map(l => l.xero_invoice_number).filter(Boolean))];

  const submitAdjustment = async () => {
    setAdjBusy(true); setAdjErr("");
    try {
      await api.post(`/month-end/${year}/${month}/adjustments`, {
        amount: Number(adjAmount),
        reason: adjReason.trim(),
      });
      setAdjOpen(false); setAdjAmount(""); setAdjReason("");
      load();
    } catch (e) { setAdjErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setAdjBusy(false); }
  };

  return (
    <div data-testid="month-end-page">
      <PageHeader
        title="Month-end billing"
        subtitle={`${data.month_label} · ${locked ? `Approved by ${data.approved_by_name} · ${formatApprovedAt(data.approved_at)}` : "waiting for someone to check and approve"}`}
        actions={
          <>
            <button className="ach-btn ach-btn--grey" onClick={()=>shiftMonth(-1)} data-testid="btn-prev-month">← Compare with last month</button>
            <button className="ach-btn ach-btn--ghost" onClick={()=>setSendOpen(true)} data-testid="btn-send-summary">✉ Send email summary…</button>
            <button className="ach-btn" onClick={openSendConfirm} disabled={locked || busy || !canSend || sendableInvoiceCount === 0} data-testid="btn-send-to-xero">
              {busy ? "Working…" : (locked ? "Month closed" : (sendableInvoiceCount === 0 ? "Nothing to send" : `Send ${sendableInvoiceCount} to Xero →`))}
            </button>
            <button className="ach-btn ach-btn--primary" onClick={openCloseConfirm} disabled={locked || busy || !canClose} data-testid="btn-close-month">
              {locked ? "Already closed" : (busy ? "Closing…" : "Close month")}
            </button>
          </>
        }
      />

      {err && (
        <div className="ach-card" style={{marginBottom: 12, borderColor: "var(--ach-red)", background: "var(--ach-red-tint)"}} data-testid="send-close-error">
          <div style={{color: "var(--ach-red)", fontWeight: 600}}>Couldn&apos;t complete: {err}</div>
        </div>
      )}

      {sendResult && (
        <div className="ach-card" style={{marginBottom: 16, borderColor: "var(--ach-green)", background: "var(--ach-green-tint)"}} data-testid="send-success">
          <div style={{color: "var(--ach-green)", fontWeight: 600}}>
            ✓ {sendResult.message || `Sent ${sendResult.posted || 0} lines across ${sendResult.customers_billed || 0} customer invoice(s).`}
          </div>
          {sendResult.results && sendResult.results.length > 0 && (
            <ul style={{margin: "6px 0 0", paddingLeft: 20, fontSize: 13, color: "var(--ach-text-1)"}}>
              {sendResult.results.map((r, i) => (
                <li key={i} style={{color: r.ok ? "var(--ach-text-1)" : "var(--ach-red)"}}>
                  {r.customer_name}: {r.ok
                    ? <>invoice <strong>{r.invoice_number}</strong> — ${r.total?.toFixed(2)}</>
                    : <>failed — {r.error}</>}
                </li>
              ))}
            </ul>
          )}
          {sendResult.skipped && (sendResult.skipped.already_sent?.length || sendResult.skipped.not_reviewed?.length || sendResult.skipped.not_found?.length) ? (
            <div style={{fontSize: 11, color: "var(--ach-text-2)", marginTop: 6}}>
              Skipped: {sendResult.skipped.already_sent?.length || 0} already sent, {sendResult.skipped.not_reviewed?.length || 0} not ticked, {sendResult.skipped.not_found?.length || 0} not on this run
            </div>
          ) : null}
        </div>
      )}
      {closeResult && (
        <div className="ach-card" style={{marginBottom: 16, borderColor: "var(--ach-green)", background: "var(--ach-green-tint)"}} data-testid="close-success">
          <div style={{color: "var(--ach-green)", fontWeight: 600}}>
            ✓ Closed by {closeResult.approved_by} on {formatApprovedAt(closeResult.approved_at)}. {closeResult.invoices_generated.length} entity total{closeResult.invoices_generated.length === 1 ? "" : "s"} generated. Month is now locked.
          </div>
          {closeResult.unsent_at_close > 0 && (
            <div style={{fontSize: 12, color: "var(--ach-text-2)", marginTop: 6}}>{closeResult.unsent_at_close} line{closeResult.unsent_at_close === 1 ? "" : "s"} were unsent at close.</div>
          )}
        </div>
      )}

      {locked && data.status === "locked" && (
        <MonthEndXeroRetry year={year} month={month} lines={data.lines} onDone={load} />
      )}
      <div className="ach-kpi-row" style={{gridTemplateColumns: "repeat(4, minmax(0, 1fr))"}} data-testid="me-kpis">
        <KpiCard tone="maroon" number={money0(remainingTotal)} label={sentLines.length > 0 ? `Still to invoice · ${money0(sentTotal)} already sent` : `All charges for ${data.month_label}`} testid="me-kpi-total" />
        <KpiCard tone="neutral" number={remainingLines.length} label={sentLines.length > 0 ? `Machines still to charge · ${sentLines.length} already sent` : "Machines being charged"} testid="me-kpi-lines" />
        <KpiCard tone="green" number={t.ok_count} label="Everything looks fine" testid="me-kpi-ok" />
        <KpiCard tone="amber" number={t.review_count} label="Lines that need checking" testid="me-kpi-review" />
      </div>

      {sentLines.length > 0 && (
        <div className="ach-card" style={{marginBottom: 16, borderColor: "var(--ach-green)"}} data-testid="me-already-sent-card">
          <h2 className="ach-card__title" style={{margin: 0}}>
            Already sent to Xero this month
            <small style={{marginLeft: 8, color: "var(--ach-text-2)"}}>
              {sentLines.length} line{sentLines.length === 1 ? "" : "s"} · {sentInvoiceNumbers.length} invoice{sentInvoiceNumbers.length === 1 ? "" : "s"} · {money0(sentTotal)}
            </small>
          </h2>
          <div className="ach-table-wrap" style={{marginTop: 10}}>
            <table className="ach-table">
              <thead>
                <tr>
                  <th style={{width: 100}}>Machine</th>
                  <th>Customer</th>
                  <th>Xero invoice</th>
                  <th>Sent at</th>
                  <th className="money">Charge (ex-GST)</th>
                </tr>
              </thead>
              <tbody>
                {sentLines
                  .slice()
                  .sort((a, b) => (a.xero_posted_at || "").localeCompare(b.xero_posted_at || ""))
                  .map(l => (
                  <tr key={`sent-${l.hire_id}`} data-testid={`me-sent-row-${l.unit_number}`}>
                    <td><strong>{l.unit_number}</strong></td>
                    <td>{l.customer_name}</td>
                    <td style={{color: "var(--ach-green)", fontWeight: 600}} data-testid={`me-sent-inv-${l.unit_number}`}>
                      {l.xero_invoice_number || "—"}
                    </td>
                    <td style={{fontSize: 12, color: "var(--ach-text-2)"}}>{shortDate(l.xero_posted_at)}</td>
                    <td className="money"><strong>{money(l.amount)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="ach-card" style={{marginBottom: 16}} data-testid="me-charges-table">
        <h2 className="ach-card__title">This month&apos;s charges <small>flagged lines are on top; everything else is standard × 4.33</small></h2>
        {/* Legend — makes it obvious the two columns are different things. */}
        <div style={{display:"flex", gap:16, alignItems:"center", fontSize:11, color:"var(--ach-text-2)", padding:"0 4px 10px"}} data-testid="me-legend">
          <span style={{display:"inline-flex", alignItems:"center", gap:6}}>
            <span className="ach-alert__chip tone-green" style={{width: 18, height: 18, fontSize: 8, borderRadius: 999}}>●</span>
            <span><strong>STATUS</strong> — everything looks fine</span>
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:6}}>
            <span className="ach-alert__chip tone-amber" style={{width: 18, height: 18, fontSize: 11, borderRadius: 4}}>⚠</span>
            <span>needs checking</span>
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:6, marginLeft: 8, borderLeft:"1px solid var(--ach-row-divider)", paddingLeft: 16}}>
            <input type="checkbox" readOnly checked style={{margin:0}} tabIndex={-1} />
            <span><strong>REVIEWED</strong> — tick each line once you've confirmed it</span>
          </span>
        </div>
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                <th style={{width: 40, textAlign:"center", fontSize: 10, letterSpacing: 0.4, color:"var(--ach-text-2)"}} title="Status">STATUS</th>
                <th style={{width: 60, textAlign:"center", fontSize: 10, letterSpacing: 0.4, color:"var(--ach-text-2)"}} title="Tick to mark this line reviewed">REVIEWED</th>
                <th>Machine</th>
                <th>Customer</th>
                <th>How it&apos;s billed</th>
                <th>The maths</th>
                <th className="money">Charge</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.lines
                .slice()
                .sort((a,b) => (b.needs_review - a.needs_review) || a.unit_number.localeCompare(b.unit_number))
                .map(l => (
                <tr key={l.hire_id} className={l.needs_review ? "flagged" : ""} data-testid={`me-line-${l.unit_number}`}>
                  <td>
                    {/* Status indicator — deliberately NOT a tick, so it can't be mistaken
                        for the reviewed checkbox in the next column. */}
                    {l.needs_review ? (
                      <span
                        className="ach-alert__chip tone-amber"
                        style={{width: 20, height: 20, fontSize: 12, borderRadius: 4}}
                        title="Needs checking — part-month, stand-down, extra charges or an override"
                        aria-label="Needs checking"
                        data-testid={`me-status-${l.unit_number}`}
                      >⚠</span>
                    ) : (
                      <span
                        className="ach-alert__chip tone-green"
                        style={{width: 20, height: 20, fontSize: 8, borderRadius: 999}}
                        title="Everything looks fine — standard × 4.33"
                        aria-label="OK"
                        data-testid={`me-status-${l.unit_number}`}
                      >●</span>
                    )}
                  </td>
                  <td style={{textAlign:"center"}}>
                    <input type="checkbox"
                      checked={!!l.reviewed}
                      disabled={locked || !!l.xero_invoice_id}
                      onChange={async (e) => {
                        try {
                          await api.post(`/month-end/${year}/${month}/lines/${l.hire_id}/reviewed`, { reviewed: e.target.checked });
                          load();
                        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
                      }}
                      data-testid={`me-reviewed-${l.unit_number}`}
                      title={l.reviewed_by_name ? `Reviewed by ${l.reviewed_by_name}` : "Tick to mark this line reviewed"}
                    />
                  </td>
                  <td>
                    <div className="unit-num">{l.unit_number}</div>
                    <div className="subtitle">{l.owning_company}</div>
                  </td>
                  <td>{l.customer_name}</td>
                  <td><StatusPill tone={pillTone(l.billing_pill)}>{l.billing_pill.toUpperCase()}</StatusPill></td>
                  <td style={{fontSize: 12, color: "var(--ach-text-2)"}}>
                    {l.calculation}
                    {l.review_reason && <div style={{color: "var(--ach-amber)", fontWeight: 500, marginTop: 2}}>{l.review_reason}</div>}
                    {l.xero_invoice_number && (
                      <div style={{color:"var(--ach-green)", fontWeight:600, marginTop:2}} data-testid={`me-line-xero-${l.unit_number}`}>
                        ✓ Xero {l.xero_invoice_number}
                      </div>
                    )}
                    {l.xero_post_error && !l.xero_invoice_id && (
                      <div style={{color:"var(--ach-red)", fontWeight:500, marginTop:2}} data-testid={`me-line-xero-err-${l.unit_number}`}>
                        Xero: {l.xero_post_error.slice(0, 100)}
                      </div>
                    )}
                  </td>
                  <td className="money"><strong>{money(l.amount)}</strong></td>
                  <td>
                    <div className="ach-row-actions__inner">
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>nav(`/hires?edit=${l.hire_id}`)} data-testid={`me-edit-${l.unit_number}`}
                        title="Open this hire to correct it">Edit hire →</button>
                      {!locked && !l.xero_invoice_id && (
                        <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={()=>setOverrideFor(l)} data-testid={`me-override-${l.unit_number}`}
                          title="Zero out specific charge lines on this invoice (one-off, this month only)">Adjust lines…</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} style={{textAlign:"right", padding:"14px 12px", fontWeight: 600}}>Total for {data.month_label}</td>
                <td className="money" style={{fontWeight: 700, fontSize: 14}}>{money(t.total)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {(t.adjustments_total !== 0 || locked) && (
        <div className="ach-card" style={{marginBottom: 16}} data-testid="me-adjustments-card">
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
            <div>
              <h2 className="ach-card__title" style={{margin:0}}>Adjustments &amp; credits</h2>
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:4}}>
                Fixes and credits added after this month was locked. The original charges above stay exactly as they were.
              </div>
            </div>
            {canAdjust && (
              <button className="ach-btn ach-btn--primary" onClick={()=>setAdjOpen(true)} data-testid="btn-add-adjustment">+ Add an adjustment</button>
            )}
          </div>
          {(data.adjustments || []).length === 0 ? (
            <div style={{padding:16, textAlign:"center", color:"var(--ach-text-2)", fontSize:13}}>
              {locked ? "None yet." : "None yet — this month has to be approved first."}
            </div>
          ) : (
            <div className="ach-table-wrap">
              <table className="ach-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>Reason</th>
                    <th className="money">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.adjustments.map(a => (
                    <tr key={a.id} data-testid={`adj-row-${a.id}`}>
                      <td>{shortDate(a.added_at)}</td>
                      <td>{a.added_by_name}</td>
                      <td>{a.reason}</td>
                      <td className="money" style={{color: a.amount < 0 ? "var(--ach-red)" : "var(--ach-charcoal)"}}>
                        {a.amount < 0 ? "−" : "+"}{money(Math.abs(a.amount))}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} style={{textAlign:"right", padding:"14px 12px", fontWeight:600}}>New total (charges + adjustments)</td>
                    <td className="money" style={{fontWeight:700, fontSize:14}} data-testid="me-grand-total">{money(t.grand_total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="ach-2col-even">
        <div className="ach-card" data-testid="card-company-invoices">
          <h2 className="ach-card__title">Entity reference totals <small>crane hire only (code 200)</small></h2>
          <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:12, fontStyle:"italic"}}>
            Not sent to Xero. Raise the real inter-entity invoices in Xero yourself. Transport and ancillary billed directly to the customer by ACH.
          </div>
          <div className="ach-table-wrap">
            <table className="ach-table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>To</th>
                  <th>Lines</th>
                  <th className="money">Crane hire (code 200)</th>
                  <th className="money">Excluded</th>
                </tr>
              </thead>
              <tbody>
                {data.company_split
                  .filter(s => s.company !== "All Cranes (Hire)")
                  .map(s => {
                    const hasBreakdown = s.excluded_total !== undefined && s.excluded_total !== null;
                    const excluded = hasBreakdown ? s.excluded_total : 0;
                    const breakdown = s.excluded_breakdown || {};
                    const bits = Object.entries(breakdown)
                      .filter(([_, v]) => v > 0)
                      .map(([ct, v]) => `${ct} ${money(v)}`)
                      .join(" · ");
                    return (
                      <tr key={s.company} data-testid={`entity-row-${s.company}`}>
                        <td>{s.company} {s.company === "Hyjac" && <StatusPill tone="blue">EMAIL SUMMARY</StatusPill>}</td>
                        <td>All Cranes (Hire)</td>
                        <td>{s.lines}</td>
                        <td className="money"><strong>{money(s.total)}</strong></td>
                        <td className="money" style={{color:"var(--ach-text-2)", fontSize:12}}>
                          {hasBreakdown && excluded > 0
                            ? <span title={bits}>{money(excluded)} <span style={{opacity:0.7}}>({bits})</span></span>
                            : (hasBreakdown ? <span style={{opacity:0.5}}>—</span>
                                            : <span style={{opacity:0.5}} title="Closed before 7 Sep 2026 — crane and ancillary combined">combined</span>)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ach-card" data-testid="card-what-happens">
          <h2 className="ach-card__title">What Send and Close actually do</h2>
          {[
            "Post every charge into Xero",
            "Record a reference total of what each owning company is owed by All Cranes (Hire) — crane hire only (code 200), stored locally for Todd to see, not sent to Xero",
            "Email a summary to Hyjac (they're an external supplier)",
            "Lock this month — any later fixes are recorded as an adjustment line on this same month (add or credit)",
            "Record who approved it, and exactly when",
          ].map((t, i) => (
            <div key={i} style={{display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom: i < 4 ? "1px solid var(--ach-row-divider)" : 0}}>
              <StatusPill tone="neutral">AUTOMATIC</StatusPill>
              <span style={{fontSize: 13}}>{t}</span>
            </div>
          ))}
          <div style={{fontSize: 12, color: "var(--ach-text-2)", marginTop: 12, fontStyle: "italic"}}>
            Approved by: {data.approved_by_name || "waiting"} · Every line shows its maths — that&apos;s why anyone can press this button, not just Todd.
          </div>
        </div>
      </div>

      {sendConfirmOpen && (
        <div className="ach-modal-backdrop" data-testid="confirm-send-dialog" onClick={()=>setSendConfirmOpen(false)}>
          <div className="ach-modal" style={{maxWidth: 640, maxHeight: "85vh", display:"flex", flexDirection:"column"}} onClick={(e)=>e.stopPropagation()}>
            <div className="ach-modal__header">
              <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Approve month-end for {data.month_label}?</h2>
            </div>
            <div className="ach-modal__body" style={{overflowY:"auto"}}>
              <div style={{padding: 12, background: "var(--ach-maroon-tint)", borderLeft: "3px solid var(--ach-maroon)", marginBottom: 14, fontSize: 14}} data-testid="confirm-headline">
                <strong>{sendableInvoiceCount} invoice{sendableInvoiceCount === 1 ? "" : "s"} totalling {money(sendableTotal)}</strong> will be created in Xero as <strong>DRAFT</strong>.
                <div style={{fontSize: 11, color: "var(--ach-text-2)", marginTop: 4, fontWeight: 400}}>Amounts shown are ex-GST as stored in ACH. Xero adds 10% GST automatically, so each draft will read ~10% higher.</div>
              </div>
              <p style={{margin:"0 0 10px", fontSize:12, color:"var(--ach-text-2)", lineHeight:1.5}}>
                Draft invoices are <strong>not emailed</strong> — someone still has to open each one in Xero and press Approve → Email to actually send it to the customer.
                After you press the button below the month will lock. Any later fixes need to be recorded as an adjustment on this same month (add or credit).
              </p>
              <div style={{border: "1px solid var(--ach-row-divider)", borderRadius: 6, overflow:"hidden"}} data-testid="confirm-customer-list">
                <div style={{padding: "6px 10px", background: "var(--ach-bg-soft)", fontSize: 11, fontWeight: 600, color: "var(--ach-text-2)", letterSpacing: 0.4, display:"flex", justifyContent:"space-between"}}>
                  <span>CUSTOMER ({sendableInvoiceCount})</span>
                  <span>TOTAL</span>
                </div>
                {sendableCustomers.map((c, i) => (
                  <div key={i} style={{padding: "7px 10px", fontSize: 12, display:"flex", justifyContent:"space-between", borderTop: i === 0 ? "none" : "1px solid var(--ach-row-divider)"}}>
                    <span>{c.name}{c.count > 1 ? <span style={{color:"var(--ach-text-2)"}}> · {c.count} lines</span> : null}</span>
                    <span style={{fontVariantNumeric:"tabular-nums"}}>{money(c.total)}</span>
                  </div>
                ))}
                <div style={{padding: "8px 10px", background: "var(--ach-bg-soft)", fontSize: 13, fontWeight: 700, display:"flex", justifyContent:"space-between", borderTop: "1px solid var(--ach-row-divider)"}}>
                  <span>Grand total</span>
                  <span style={{fontVariantNumeric:"tabular-nums"}} data-testid="confirm-grand-total">{money(sendableTotal)}</span>
                </div>
              </div>
            </div>
            <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button className="ach-btn ach-btn--grey" onClick={()=>setSendConfirmOpen(false)} data-testid="confirm-send-cancel">Cancel</button>
              <button className="ach-btn ach-btn--primary" onClick={doSend} data-testid="confirm-send-go">
                Create {sendableInvoiceCount} invoice{sendableInvoiceCount === 1 ? "" : "s"} in Xero →
              </button>
            </div>
          </div>
        </div>
      )}
      {closeConfirmOpen && (
        <div className="ach-modal-backdrop" data-testid="confirm-close-dialog" onClick={()=>setCloseConfirmOpen(false)}>
          <div className="ach-modal" style={{maxWidth: 560}} onClick={(e)=>e.stopPropagation()}>
            <div className="ach-modal__header">
              <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Close month-end for {data.month_label}?</h2>
            </div>
            <div className="ach-modal__body">
              <p style={{margin:"0 0 10px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
                Closing locks this month and generates the entity/company-to-company totals. It does <strong>not</strong> send any invoices to Xero — sending is a separate action.
                After closing, any later fixes need to be recorded as an adjustment on this same month (add or credit).
              </p>
              {unsentAtClose.length > 0 && (
                <div style={{padding: 10, background: "var(--ach-amber-tint)", borderLeft: "3px solid var(--ach-amber)", marginBottom: 10, fontSize: 12}} data-testid="close-unsent-warning">
                  <div style={{fontWeight: 600, marginBottom: 4}}>{unsentAtClose.length} line{unsentAtClose.length === 1 ? "" : "s"} haven&apos;t been sent to Xero yet:</div>
                  <div style={{color: "var(--ach-text-2)"}}>{unsentUnits.join(", ")}</div>
                  <label style={{display:"flex", gap:8, alignItems:"center", marginTop: 8, cursor:"pointer"}}>
                    <input type="checkbox" checked={closeAckUnsent} onChange={(e)=>setCloseAckUnsent(e.target.checked)} data-testid="close-ack-unsent" />
                    <span>I understand these lines will NOT go to Xero — closing the month anyway.</span>
                  </label>
                </div>
              )}
            </div>
            <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button className="ach-btn ach-btn--grey" onClick={()=>setCloseConfirmOpen(false)} data-testid="confirm-close-cancel">Cancel</button>
              <button className="ach-btn ach-btn--primary" onClick={doClose} disabled={unsentAtClose.length > 0 && !closeAckUnsent} data-testid="confirm-close-go">
                Close month
              </button>
            </div>
          </div>
        </div>
      )}


      {overrideFor && <LineOverrideDialog line={overrideFor} monthStr={`${year}-${String(month).padStart(2,'0')}`}
        onClose={()=>setOverrideFor(null)}
        onSaved={()=>{ setOverrideFor(null); load(); }} />}

      {sendOpen && (
        <SendSummaryDialog
          data={data}
          year={year}
          month={month}
          onClose={()=>setSendOpen(false)}
        />
      )}

      {adjOpen && <AdjustmentDialog
        monthLabel={data.month_label}
        adjAmount={adjAmount} setAdjAmount={setAdjAmount}
        adjReason={adjReason} setAdjReason={setAdjReason}
        adjErr={adjErr} adjBusy={adjBusy}
        onClose={()=>setAdjOpen(false)}
        onSave={submitAdjustment}
      />}
    </div>
  );
}
